/**
 * Owner Inventory Dashboard — Smart Inventory Phase 1.
 *
 * Route: /food-pulse/owner/inventory
 * Auth : Owner/manager/staff of a business (fp_business_directory).
 *
 * Layout
 * ------
 *   Header row: business picker + "+ Receive Lot" primary action
 *   Action Center: at-risk lots ranked by expiry/condition with one-tap promote
 *   Lot List: filter by status/at-risk, quick jump to lot detail
 *   Dashboard rollup: last-30-day event counts (sold, spoiled, donated…)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, PackagePlus, Package, RefreshCw, Loader2, TrendingDown, Recycle, Gift, Tag, ArrowRight, Printer, BarChart3 } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SmartInventoryFeatures from "@/components/SmartInventoryFeatures";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const AMBER = "#E4A140";

function Chip({ children, color = "#E9D8B4" }) {
  return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold" style={{ background: color, color: GREEN }}>{children}</span>;
}

function LotCard({ lot, onPromote }) {
  const days = lot.days_to_expiry;
  const risk = lot.at_risk;
  return (
    <div className="rounded-2xl border p-4 flex flex-col gap-2" style={{ borderColor: "#E9D8B4", background: risk ? "#FEF3C7" : "white" }} data-testid={`lot-card-${lot.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold" style={{ color: GREEN }}>{lot.product_name || "Unnamed lot"}</div>
          <div className="text-xs text-gray-600">{lot.product_category || "—"} · Lot {lot.lot_no || lot.id.slice(0, 6)}</div>
        </div>
        {risk && <Chip color="#FDE68A"><AlertTriangle size={12} /> At risk</Chip>}
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <Chip>{lot.current_qty ?? 0} {lot.unit || "each"}</Chip>
        {days !== null && days !== undefined && <Chip color={days <= 1 ? "#FCA5A5" : days <= 3 ? "#FDE68A" : "#BBF7D0"}>{days}d to expiry</Chip>}
        {lot.condition_score !== undefined && <Chip>Condition {lot.condition_score}</Chip>}
      </div>
      {lot.suggested_actions && (
        <div className="flex flex-wrap gap-1 mt-1">
          {lot.suggested_actions.map((a, i) => (
            <button
              key={i}
              onClick={() => onPromote(lot, a)}
              className="text-xs px-2 py-1 rounded-full border font-semibold"
              style={{ borderColor: GREEN, color: GREEN }}
              data-testid={`action-btn-${lot.id}-${i}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mt-2">
        <Link to={`/food-pulse/owner/inventory/lots/${lot.id}`} className="text-xs font-semibold flex items-center gap-1" style={{ color: GREEN }} data-testid={`view-lot-${lot.id}`}>
          View lot <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}

async function fetchMyBusinesses() {
  // Prefer authenticated business memberships
  try {
    const r = await http.get("/businesses/mine");
    return r.data.items || r.data || [];
  } catch { /* fall through */ }
  return [];
}

export default function OwnerInventoryDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [params, setParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [businessId, setBusinessId] = useState(params.get("business_id") || "");
  const [lots, setLots] = useState([]);
  const [actionCenter, setActionCenter] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all");   // all | at_risk
  const [promoting, setPromoting] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetchMyBusinesses().then(setBusinesses);
  }, [user]);

  useEffect(() => {
    if (businesses.length > 0 && !businessId) {
      setBusinessId(businesses[0].id);
      setParams({ business_id: businesses[0].id });
    }
  }, [businesses, businessId, setParams]);

  const reload = async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [lotsR, acR, dR] = await Promise.all([
        http.get(`/inventory/lots`, { params: { business_id: businessId, at_risk: tab === "at_risk" } }),
        http.get(`/inventory/action-center`, { params: { business_id: businessId } }),
        http.get(`/inventory/dashboard/${businessId}`),
      ]);
      setLots(lotsR.data.items || []);
      setActionCenter(acR.data.items || []);
      setRollup(dR.data);
    } catch (e) { console.warn(e); }
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [businessId, tab]);

  const promote = async (lot, action) => {
    if (action.kind === "transfer") { alert("Transfer flow coming soon"); return; }
    setPromoting(lot.id);
    const endsAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      const r = await http.post(`/inventory/lots/${lot.id}/promote`, {
        kind: action.kind, discount_pct: action.discount_pct || 30, ends_at: endsAt,
        title: `${lot.product_name} — ${action.label}`,
      });
      alert(`Deal live! /food-pulse/deals/${r.data.deal.slug}`);
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Promote failed"); }
    setPromoting(null);
  };

  if (authLoading) return <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!user) {
    return (
      <main className="min-h-screen" style={{ background: CREAM }} data-testid="owner-inventory-signedout">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <SmartInventoryFeatures />
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="owner-inventory-dashboard">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black" style={{ color: GREEN }}>Smart Inventory</h1>
            <p className="text-sm text-gray-600">Lot-first tracking · reduce waste · one-tap promote</p>
          </div>
          <div className="flex items-center gap-2">
            {businesses.length > 1 && (
              <select value={businessId} onChange={(e) => { setBusinessId(e.target.value); setParams({ business_id: e.target.value }); }} className="border rounded-full px-3 py-2 text-sm" data-testid="business-picker">
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            <Link to={`/food-pulse/owner/inventory/receive?business_id=${businessId}`} className="rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2" style={{ background: GREEN, color: CREAM }} data-testid="receive-lot-btn">
              <PackagePlus size={16} /> Receive lot
            </Link>
            <Link to={`/food-pulse/owner/inventory/qr-sheet?business_id=${businessId}`} className="rounded-full border px-4 py-2 text-sm font-semibold flex items-center gap-2" style={{ borderColor: GREEN, color: GREEN }} data-testid="print-qr-sheet-btn">
              <Printer size={14} /> Print QR labels
            </Link>
            <Link to={`/food-pulse/owner/inventory/scans?business_id=${businessId}`} className="rounded-full border px-4 py-2 text-sm font-semibold flex items-center gap-2" style={{ borderColor: GREEN, color: GREEN }} data-testid="scans-btn">
              <BarChart3 size={14} /> Scans
            </Link>
            <button onClick={reload} className="rounded-full border px-3 py-2 text-sm" style={{ borderColor: GREEN, color: GREEN }} data-testid="reload-btn">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </header>

        {actionCenter.length > 0 && (
          <section className="mb-8" data-testid="action-center-section">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2" style={{ color: GREEN }}>
              <AlertTriangle size={18} style={{ color: AMBER }} /> Action Center — today's at-risk lots
            </h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {actionCenter.slice(0, 6).map((l) => <LotCard key={l.id} lot={l} onPromote={promote} />)}
            </div>
          </section>
        )}

        {rollup && (
          <section className="mb-8 grid gap-3 md:grid-cols-4">
            <div className="rounded-2xl p-4 bg-white shadow-sm border" style={{ borderColor: "#E9D8B4" }}>
              <div className="text-xs text-gray-600">Active lots</div>
              <div className="text-2xl font-black" style={{ color: GREEN }}>{rollup.active_lots}</div>
            </div>
            <div className="rounded-2xl p-4 bg-white shadow-sm border" style={{ borderColor: "#E9D8B4" }}>
              <div className="text-xs text-gray-600">Closed (7d)</div>
              <div className="text-2xl font-black" style={{ color: GREEN }}>{rollup.closed_last_7d}</div>
            </div>
            <div className="rounded-2xl p-4 bg-white shadow-sm border" style={{ borderColor: "#E9D8B4" }}>
              <div className="text-xs text-gray-600 flex items-center gap-1"><TrendingDown size={12} /> Spoiled (30d)</div>
              <div className="text-2xl font-black text-red-700">{rollup.last_30d_events?.spoiled?.qty ?? 0}</div>
            </div>
            <div className="rounded-2xl p-4 bg-white shadow-sm border" style={{ borderColor: "#E9D8B4" }}>
              <div className="text-xs text-gray-600 flex items-center gap-1"><Gift size={12} /> Donated (30d)</div>
              <div className="text-2xl font-black" style={{ color: GREEN }}>{rollup.last_30d_events?.donated?.qty ?? 0}</div>
            </div>
          </section>
        )}

        <section>
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => setTab("all")} className={`px-3 py-1 rounded-full text-xs font-semibold border ${tab === "all" ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={tab !== "all" ? { borderColor: GREEN, color: GREEN } : {}} data-testid="tab-all">All lots</button>
            <button onClick={() => setTab("at_risk")} className={`px-3 py-1 rounded-full text-xs font-semibold border ${tab === "at_risk" ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={tab !== "at_risk" ? { borderColor: GREEN, color: GREEN } : {}} data-testid="tab-at-risk">At risk</button>
          </div>
          {lots.length === 0 ? (
            <div className="rounded-2xl border p-10 text-center bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="lots-empty-state">
              <Package className="mx-auto mb-2 text-gray-400" size={40} />
              <div className="text-gray-600 mb-3">No lots {tab === "at_risk" ? "at risk" : "yet"}.</div>
              <Link to={`/food-pulse/owner/inventory/receive?business_id=${businessId}`} className="rounded-full px-4 py-2 text-sm font-semibold" style={{ background: GREEN, color: CREAM }}>Receive your first lot</Link>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="lots-grid">
              {lots.map((l) => <LotCard key={l.id} lot={l} onPromote={promote} />)}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
